// Local types for @uniswap/token-lists that work with Vite
// The beta version doesn't export types properly

export interface TokenInfo {
  readonly chainId: number
  readonly address: string
  readonly name: string
  readonly decimals: number
  readonly symbol: string
  readonly logoURI?: string
  readonly tags?: string[]
  readonly extensions?: Record<string, unknown>
}

export interface Version {
  readonly major: number
  readonly minor: number
  readonly patch: number
}

export interface Tags {
  readonly [tagId: string]: {
    readonly name: string
    readonly description: string
  }
}

export interface TokenList {
  readonly name: string
  readonly timestamp: string
  readonly version: Version
  readonly tokens: TokenInfo[]
  readonly tokenMap?: Record<string, TokenInfo>
  readonly keywords?: string[]
  readonly tags?: Tags
  readonly logoURI?: string
}

export enum VersionUpgrade {
  NONE = 'NONE',
  PATCH = 'PATCH',
  MINOR = 'MINOR',
  MAJOR = 'MAJOR',
}

export function getVersionUpgrade(base: Version, update: Version): VersionUpgrade {
  if (update.major > base.major) return VersionUpgrade.MAJOR
  if (update.major < base.major) return VersionUpgrade.NONE
  if (update.minor > base.minor) return VersionUpgrade.MINOR
  if (update.minor < base.minor) return VersionUpgrade.NONE
  if (update.patch > base.patch) return VersionUpgrade.PATCH
  return VersionUpgrade.NONE
}

export function minVersionBump(base: TokenInfo[], updated: TokenInfo[]): VersionUpgrade {
  const baseAddresses = new Set(base.map(t => `${t.chainId}-${t.address.toLowerCase()}`))
  const updatedAddresses = new Set(updated.map(t => `${t.chainId}-${t.address.toLowerCase()}`))

  // Check for removals (MAJOR)
  for (const addr of baseAddresses) {
    if (!updatedAddresses.has(addr)) {
      return VersionUpgrade.MAJOR
    }
  }

  // Check for additions (MINOR)
  for (const addr of updatedAddresses) {
    if (!baseAddresses.has(addr)) {
      return VersionUpgrade.MINOR
    }
  }

  // Check for changes (PATCH)
  const baseMap = new Map(base.map(t => [`${t.chainId}-${t.address.toLowerCase()}`, t]))
  for (const token of updated) {
    const key = `${token.chainId}-${token.address.toLowerCase()}`
    const baseToken = baseMap.get(key)
    if (baseToken && JSON.stringify(baseToken) !== JSON.stringify(token)) {
      return VersionUpgrade.PATCH
    }
  }

  return VersionUpgrade.NONE
}

export function diffTokenLists(
  base: TokenInfo[],
  updated: TokenInfo[]
): { added: TokenInfo[]; removed: TokenInfo[]; changed: TokenInfo[] } {
  const baseMap = new Map(base.map(t => [`${t.chainId}-${t.address.toLowerCase()}`, t]))
  const updatedMap = new Map(updated.map(t => [`${t.chainId}-${t.address.toLowerCase()}`, t]))

  const added: TokenInfo[] = []
  const removed: TokenInfo[] = []
  const changed: TokenInfo[] = []

  for (const [key, token] of updatedMap) {
    if (!baseMap.has(key)) {
      added.push(token)
    } else if (JSON.stringify(baseMap.get(key)) !== JSON.stringify(token)) {
      changed.push(token)
    }
  }

  for (const [key, token] of baseMap) {
    if (!updatedMap.has(key)) {
      removed.push(token)
    }
  }

  return { added, removed, changed }
}
